import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Flex,
  Text,
  VStack,
  Icon,
  Image,
  useColorModeValue,
  Drawer,
  DrawerContent,
  DrawerOverlay,
  DrawerCloseButton,
  DrawerBody,
  useBreakpointValue,
  Divider,
} from '@chakra-ui/react';
import {
  MdAdminPanelSettings,
  MdMonitorHeart,
  MdLeaderboard,
  MdDashboard,
  MdPeople,
  MdSettings,
  MdDescription,
  MdPersonAdd,
  MdScience,
  MdDomain,
  MdHandyman,
  MdAssessment,
  MdSupportAgent,
  MdFeedback,
  MdMenuBook,
  MdAttachMoney,
  MdGroups,
} from 'react-icons/md';
import { LanguageSwitcher } from '../LanguageSwitcher';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavItem {
  translationKey: string;
  path: string;
  icon: typeof MdMonitorHeart;
}

  interface NavItemWithRoles extends NavItem {
    allowedRoles?: ('superadmin' | 'admin' | 'sales' | 'support' | 'accountant' | 'sales')[];
  }

  const navItems: NavItemWithRoles[] = [
    // Available to all roles
    { translationKey: 'nav.dashboard', path: '/admin/dashboard', icon: MdDashboard, allowedRoles: ['superadmin', 'admin', 'sales', 'support', 'accountant', 'sales'] },
    
    // Sales only - Lead & Demo Management
    { translationKey: 'nav.sales', path: '/admin/sales/leads', icon: MdLeaderboard, allowedRoles: ['superadmin', 'admin', 'sales', 'sales'] },
    { translationKey: 'nav.salesMetrics', path: '/admin/sales/metrics', icon: MdLeaderboard, allowedRoles: ['superadmin', 'admin', 'sales', 'sales'] },
    { translationKey: 'nav.demoCalendar', path: '/admin/sales/demos', icon: MdLeaderboard, allowedRoles: ['superadmin', 'admin', 'sales', 'sales'] },
    { translationKey: 'nav.automation', path: '/admin/sales/automation', icon: MdLeaderboard, allowedRoles: ['superadmin', 'admin', 'sales', 'sales'] },
    
    // Onboarding only - B2B and Landlord Onboarding
    { translationKey: 'nav.onboarding', path: '/admin/onboarding', icon: MdAdminPanelSettings, allowedRoles: ['superadmin', 'admin', 'support'] },
    { translationKey: 'nav.onboardingMetrics', path: '/admin/onboarding/metrics', icon: MdAdminPanelSettings, allowedRoles: ['superadmin', 'admin', 'support'] },
    { translationKey: 'nav.landlordOnboarding', path: '/admin/landlord-onboarding', icon: MdPersonAdd, allowedRoles: ['superadmin', 'admin', 'support'] },
    
    // Admin only - Contracts & Payments
    { translationKey: 'nav.contracts', path: '/admin/contracts', icon: MdDescription, allowedRoles: ['superadmin', 'admin', 'accountant'] },
    { translationKey: 'nav.payments', path: '/admin/payments', icon: MdAttachMoney, allowedRoles: ['superadmin', 'admin', 'accountant'] },
    
    // Admin only - Properties & Assets
    { translationKey: 'nav.properties', path: '/admin/properties', icon: MdDomain, allowedRoles: ['superadmin', 'admin'] },
    { translationKey: 'nav.assets', path: '/admin/assets', icon: MdHandyman, allowedRoles: ['superadmin', 'admin'] },
    
    // Admin & Support - Reports & Monitoring
    { translationKey: 'nav.reports', path: '/admin/reports', icon: MdAssessment, allowedRoles: ['superadmin', 'admin', 'support'] },
    { translationKey: 'nav.monitoring', path: '/admin/monitoring', icon: MdMonitorHeart, allowedRoles: ['superadmin', 'admin', 'support'] },
    
    // Support only - Support Tickets
    { translationKey: 'nav.supportTickets', path: '/admin/support/tickets', icon: MdSupportAgent, allowedRoles: ['superadmin', 'admin', 'support'] },
    { translationKey: 'nav.feedback', path: '/admin/support/feedback', icon: MdFeedback, allowedRoles: ['superadmin', 'admin', 'support'] },
    { translationKey: 'nav.knowledgeBase', path: '/admin/support/knowledge-base', icon: MdMenuBook, allowedRoles: ['superadmin', 'admin', 'support'] },
    
    // Superadmin & Admin only - Users & Settings
    { translationKey: 'nav.users', path: '/admin/users', icon: MdPeople, allowedRoles: ['superadmin', 'admin'] },
    { translationKey: 'nav.settings', path: '/admin/settings', icon: MdSettings, allowedRoles: ['superadmin'] },
    { translationKey: 'nav.demoData', path: '/admin/demo-data', icon: MdScience, allowedRoles: ['superadmin'] },
  ];function SidebarContent({ isMobile = false, onClose }: { isMobile?: boolean; onClose?: () => void }) {
  const { t } = useTranslation('common');
  const { userProfile } = useAuth();
  const location = useLocation();
  const bgColor = useColorModeValue('white', 'navy.800');
  const borderColor = useColorModeValue('gray.200', 'navy.700');
  const activeBg = useColorModeValue('brand.50', 'navy.700');
  const activeColor = useColorModeValue('brand.600', 'brand.200');
  const hoverBg = useColorModeValue('gray.100', 'navy.700');

  return (
    <Box
      bg={bgColor}
      borderRight={isMobile ? 'none' : '1px'}
      borderColor={borderColor}
      w="280px"
      h="100vh"
      position="fixed"
      left={0}
      top={0}
    >
      {/* Logo */}
      <Flex h="40" alignItems="center" mx="8" justifyContent="center">
        <Image 
          src="/dossiloc-logo1.png" 
          alt="DossiLoc" 
          h="80px"
        />
      </Flex>

      {/* Navigation */}
      <Box 
        overflowY="auto" 
        h="calc(100vh - 200px)" 
        css={{
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-track': {
            width: '6px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'gray',
            borderRadius: '24px',
          },
        }}
      >
        <VStack spacing={2} align="stretch" px={4} pb={4}>
          {navItems
            .filter((item) => {
              // If no allowedRoles specified, only show to superadmin/admin
              const allowedRoles = (item as NavItemWithRoles).allowedRoles;
              if (!allowedRoles) return false;
              
              // Check if user's role is in the allowed roles list
              const userRole = userProfile?.role;
              return userRole && allowedRoles.includes(userRole as any);
            })
            .map((item) => {
              const isActive = location.pathname === item.path;

              return (
                <Box
                  key={item.path}
                  as={RouterLink}
                  to={item.path}
                  display="flex"
                  alignItems="center"
                  p={4}
                  borderRadius="xl"
                  cursor="pointer"
                  bg={isActive ? activeBg : 'transparent'}
                  color={isActive ? activeColor : 'gray.700'}
                  fontWeight={isActive ? '700' : '600'}
                  _hover={{
                    bg: isActive ? activeBg : hoverBg,
                    textDecoration: 'none',
                    transform: 'translateX(4px)',
                  }}
                  transition="all 0.2s"
                  onClick={() => isMobile && onClose && onClose()}
                >
                  <Icon as={item.icon} fontSize="24px" mr={4} />
                  <Text fontSize="md" fontWeight="inherit">{t(item.translationKey)}</Text>
                </Box>
              );
            })}
        </VStack>
      </Box>

      {/* Language Switcher */}
      <Box position="absolute" bottom={4} left={4} right={4}>
        <Divider mb={4} />
        <LanguageSwitcher variant="button" showIcon={false} />
      </Box>
    </Box>
  );
}

export default function Sidebar({ isOpen, onClose }: SidebarProps) {
  const isMobile = useBreakpointValue({ base: true, md: false });

  if (isMobile) {
    return (
      <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerBody p={0}>
            <SidebarContent isMobile={true} onClose={onClose} />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    );
  }

  return <SidebarContent isMobile={false} />;
}
